import React from "react";
import { motion, useReducedMotion, useScroll, useSpring } from "framer-motion";
import {
  Award,
  BookOpen,
  BriefcaseBusiness,
  Building2,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  Download,
  ExternalLink,
  FileText,
  GalleryHorizontalEnd,
  GraduationCap,
  Layers3,
  Mail,
  MapPin,
  Phone,
  ShieldCheck,
  Sparkles,
  Target,
  Users,
} from "lucide-react";
import "../../styles/institutional-pages.css";

const THEME_MAP = {
  department: {
    label: "Academic Department",
    tone: "department",
    accent: "#1d4ed8",
    accent2: "#0ea5e9",
    icon: GraduationCap,
  },
  directorate: {
    label: "Directorate",
    tone: "directorate",
    accent: "#4f46e5",
    accent2: "#7c3aed",
    icon: Building2,
  },
  unit: {
    label: "Institutional Unit",
    tone: "unit",
    accent: "#047857",
    accent2: "#16a34a",
    icon: ShieldCheck,
  },
  center: {
    label: "Innovation Center",
    tone: "center",
    accent: "#0f766e",
    accent2: "#06b6d4",
    icon: Sparkles,
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 28, filter: "blur(8px)" },
  visible: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.62, ease: [0.22, 1, 0.36, 1] } },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.075, delayChildren: 0.04 } },
};

const getTheme = (type) => THEME_MAP[type] || THEME_MAP.department;

function Section({ id, eyebrow, title, icon: Icon = Layers3, children, className = "" }) {
  const reduceMotion = useReducedMotion();
  return (
    <motion.section
      id={id}
      variants={fadeUp}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.16, margin: "-80px" }}
      whileHover={reduceMotion ? undefined : { y: -3 }}
      transition={{ duration: 0.35 }}
      className={`institutional-section ${className}`}
    >
      <div className="institutional-section-heading">
        <span className="institutional-eyebrow">{eyebrow}</span>
        <h2><Icon size={24} /> {title}</h2>
      </div>
      {children}
    </motion.section>
  );
}

function Chip({ children }) {
  return <span className="institutional-chip">{children}</span>;
}

function Card({ children, className = "" }) {
  const reduceMotion = useReducedMotion();
  return (
    <motion.div
      variants={fadeUp}
      whileHover={reduceMotion ? undefined : { y: -8, scale: 1.012 }}
      whileTap={reduceMotion ? undefined : { scale: 0.992 }}
      transition={{ type: "spring", stiffness: 130, damping: 18, mass: 0.7 }}
      className={`institutional-card ${className}`}
    >
      {children}
    </motion.div>
  );
}

export default function InstitutionalShowcasePage({ data }) {
  const theme = getTheme(data?.type);
  const reduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll();
  const progressScale = useSpring(scrollYProgress, { stiffness: 120, damping: 28, mass: 0.35 });
  const TypeIcon = theme.icon;
  const quickStats = data?.quickStats || [];
  const navItems = [
    ["overview", "Overview"],
    ["leader", data?.leader?.sectionTitle || "Leadership"],
    ["offerings", data?.offeringsTitle || "Programmes"],
    ["impact", "Impact"],
    ["gallery", "Gallery"],
    ["contact", "Contact"],
  ].filter(([id]) => {
    if (id === "leader") return !!data?.leader;
    if (id === "gallery") return (data?.gallery || []).length > 0;
    return true;
  });

  return (
    <div className={`institutional-page institutional-${theme.tone}`} style={{ "--institutional-accent": theme.accent, "--institutional-accent-2": theme.accent2 }}>
      <motion.div className="institutional-scroll-progress" style={{ scaleX: progressScale }} aria-hidden="true" />
      <nav className="institutional-page-nav" aria-label="Page sections">
        <div className="institutional-page-nav-title">
          <TypeIcon size={18} />
          <span>{data?.shortName || data?.title}</span>
        </div>
        <div className="institutional-page-nav-links">
          {navItems.map(([href, label]) => <a key={href} href={`#${href}`}>{label}</a>)}
        </div>
      </nav>

      <header className="institutional-hero">
        <motion.span
          className="institutional-orb orb-one"
          animate={reduceMotion ? undefined : { y: [0, -18, 0], x: [0, 10, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          aria-hidden="true"
        />
        <motion.span
          className="institutional-orb orb-two"
          animate={reduceMotion ? undefined : { y: [0, 14, 0], x: [0, -12, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          aria-hidden="true"
        />
        <motion.img
          src={data?.heroImage || "/images/fpb.png"}
          alt=""
          className="institutional-hero-image"
          initial={reduceMotion ? false : { scale: 1.08 }}
          animate={reduceMotion ? undefined : { scale: 1.02 }}
          transition={{ duration: 3.2, ease: [0.22, 1, 0.36, 1] }}
        />
        <div className="institutional-hero-overlay" />
        <div className="institutional-hero-pattern" />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.72, ease: [0.22, 1, 0.36, 1] }}
          className="institutional-hero-content"
        >
          <div className="institutional-hero-badges">
            <span><TypeIcon size={16} /> {data?.categoryLabel || theme.label}</span>
            {data?.parentGroup && <span>{data.parentGroup}</span>}
          </div>
          <h1>{data?.title}</h1>
          <p>{data?.subtitle}</p>
          <div className="institutional-hero-actions">
            {(data?.primaryAction || data?.secondaryAction) && (
              <>
                {data?.primaryAction && <a className="institutional-btn primary" href={data.primaryAction.href}>{data.primaryAction.label}<ChevronRight size={18} /></a>}
                {data?.secondaryAction && <a className="institutional-btn ghost" href={data.secondaryAction.href}>{data.secondaryAction.label}<ExternalLink size={16} /></a>}
              </>
            )}
          </div>
        </motion.div>

        {quickStats.length > 0 && (
          <motion.div variants={stagger} initial="hidden" animate="visible" className="institutional-hero-stats">
            {quickStats.slice(0, 4).map((stat, i) => (
              <motion.div variants={fadeUp} whileHover={reduceMotion ? undefined : { y: -6, scale: 1.03 }} key={`${stat.label}-${i}`} className="institutional-stat-card">
                <strong>{stat.value}</strong>
                <span>{stat.label}</span>
              </motion.div>
            ))}
          </motion.div>
        )}
      </header>

      <main className="institutional-main">
        <Section id="overview" eyebrow="About" title={data?.overviewTitle || "Overview"} icon={BookOpen}>
          <div className="institutional-overview-grid">
            <div className="institutional-richtext">
              {(data?.overview || []).map((paragraph, i) => <p key={i}>{paragraph}</p>)}
            </div>
            <aside className="institutional-quick-info" aria-label="Quick information">
              <h3><FileText size={20} /> Quick Information</h3>
              {(data?.quickInfo || []).map((item, i) => (
                <div key={`${item.label}-${i}`} className="institutional-info-row">
                  <span>{item.label}</span>
                  <strong>{item.value}</strong>
                </div>
              ))}
            </aside>
          </div>
        </Section>

        {(data?.vision || data?.mission) && (
          <motion.section variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="institutional-vm-grid">
            {data?.vision && <Card><h3><Target size={22} /> Vision</h3><p>{data.vision}</p></Card>}
            {data?.mission && <Card><h3><CheckCircle2 size={22} /> Mission</h3><p>{data.mission}</p></Card>}
          </motion.section>
        )}

        {data?.leader && (
          <Section id="leader" eyebrow="Leadership" title={data.leader.sectionTitle || "Message from Leadership"} icon={Users}>
            <div className="institutional-leader-card">
              <div className="institutional-leader-photo-wrap">
                <motion.img
                  whileHover={{ rotate: 2, scale: 1.03 }}
                  transition={{ type: "spring", stiffness: 180, damping: 16 }}
                  src={data.leader.photo || "/images/fpb.png"}
                  alt={data.leader.name}
                  className="institutional-leader-photo"
                />
              </div>
              <div className="institutional-leader-content">
                <div className="institutional-leader-title-row">
                  <div>
                    <h3>{data.leader.name}</h3>
                    <p>{data.leader.title}</p>
                  </div>
                  {data.leader.badge && <span className="institutional-leader-badge">{data.leader.badge}</span>}
                </div>
                {data.leader.quote && <blockquote>“{data.leader.quote}”</blockquote>}
                {data.leader.bio && <p className="institutional-leader-bio">{data.leader.bio}</p>}
                {(data.leader.achievements || []).length > 0 && (
                  <div className="institutional-chip-row">
                    {data.leader.achievements.map((achievement, i) => <Chip key={i}>{achievement}</Chip>)}
                  </div>
                )}
              </div>
            </div>
          </Section>
        )}

        {(data?.offerings || []).length > 0 && (
          <Section id="offerings" eyebrow="What we offer" title={data?.offeringsTitle || "Programmes and Services"} icon={BriefcaseBusiness}>
            <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="institutional-card-grid">
              {data.offerings.map((item, i) => (
                <Card key={`${item.title}-${i}`}>
                  <div className="institutional-number">{String(i + 1).padStart(2, "0")}</div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                  {item.meta && <span className="institutional-mini-meta">{item.meta}</span>}
                </Card>
              ))}
            </motion.div>
          </Section>
        )}

        {(data?.features || []).length > 0 && (
          <Section id="impact" eyebrow="Why it matters" title={data?.featuresTitle || "Impact and Strengths"} icon={Award}>
            <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="institutional-feature-grid">
              {data.features.map((feature, i) => (
                <Card key={`${feature.title}-${i}`} className="institutional-feature-card">
                  <span className="institutional-feature-icon"><CheckCircle2 size={22} /></span>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </Card>
              ))}
            </motion.div>
          </Section>
        )}

        {(data?.timeline || []).length > 0 && (
          <Section id="timeline" eyebrow="Progress" title={data?.timelineTitle || "Milestones"} icon={CalendarDays}>
            <div className="institutional-timeline">
              {data.timeline.map((item, i) => (
                <motion.div key={`${item.year}-${i}`} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="institutional-timeline-item">
                  <span>{item.year}</span>
                  <div><h3>{item.title}</h3><p>{item.description}</p></div>
                </motion.div>
              ))}
            </div>
          </Section>
        )}

        {(data?.downloads || []).length > 0 && (
          <Section id="downloads" eyebrow="Resources" title="Downloads and Useful Links" icon={Download}>
            <div className="institutional-download-list">
              {data.downloads.map((item, i) => (
                <a key={`${item.label}-${i}`} href={item.href} className="institutional-download-card">
                  <FileText size={20} />
                  <span>{item.label}</span>
                  <ChevronRight size={18} />
                </a>
              ))}
            </div>
          </Section>
        )}

        {(data?.gallery || []).length > 0 && (
          <Section id="gallery" eyebrow="Media" title={data?.galleryTitle || "Gallery"} icon={GalleryHorizontalEnd}>
            <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="institutional-gallery-grid">
              {data.gallery.slice(0, 8).map((img, i) => (
                <motion.figure variants={fadeUp} whileHover={reduceMotion ? undefined : { y: -8 }} key={`${img.src}-${i}`} className="institutional-gallery-item">
                  <img src={img.src} alt={img.alt || `${data.title} gallery ${i + 1}`} loading="lazy" />
                  {img.caption && <figcaption>{img.caption}</figcaption>}
                </motion.figure>
              ))}
            </motion.div>
          </Section>
        )}

        {(data?.faqs || []).length > 0 && (
          <Section id="faqs" eyebrow="Help" title="Frequently Asked Questions" icon={BookOpen}>
            <div className="institutional-faq-list">
              {data.faqs.map((faq, i) => (
                <details key={`${faq.question}-${i}`}>
                  <summary>{faq.question}</summary>
                  <p>{faq.answer}</p>
                </details>
              ))}
            </div>
          </Section>
        )}

        <Section id="contact" eyebrow="Connect" title="Contact and Location" icon={MapPin}>
          <div className="institutional-contact-grid">
            <div className="institutional-contact-card">
              <h3>{data?.contact?.title || `Contact ${data?.shortName || "Office"}`}</h3>
              {data?.contact?.address && <p><MapPin size={18} /> {data.contact.address}</p>}
              {data?.contact?.phone && <p><Phone size={18} /> {data.contact.phone}</p>}
              {data?.contact?.email && <p><Mail size={18} /> {data.contact.email}</p>}
              {data?.contact?.hours && <p><CalendarDays size={18} /> {data.contact.hours}</p>}
            </div>
            <div className="institutional-cta-card">
              <h3>{data?.ctaTitle || "Need more information?"}</h3>
              <p>{data?.ctaText || "Contact the office or visit the appropriate administrative unit for current information."}</p>
              <div className="institutional-cta-actions">
                <a href={data?.contact?.email ? `mailto:${data.contact.email}` : "/contact"} className="institutional-btn primary">Send Enquiry</a>
                <a href="/contact" className="institutional-btn subtle">General Contact</a>
              </div>
            </div>
          </div>
        </Section>
      </main>
    </div>
  );
}
